import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApi } from '../api';
import { validateDraft } from '../worker/receipt-validation';
import { ReceiptSaveSession } from '../utils/receiptSave';
import ReceiptRecoveryGate from '../components/ReceiptRecoveryGate';
import DuplicateReviewModal from '../components/DuplicateReviewModal';

interface OcrResult {
    date_of_service: string | null;
    provider: string | null;
    amount: number | null;
    document_type: string;
    confidence: number;
    items: Array<{ description: string; amount: number }>;
    raw_text?: string;
    category?: string;
    model_used?: string;
    cost_estimate?: number;
    warnings?: string[];
}

interface DuplicateMatch {
    expense_id: string;
    provider: string;
    amount: number;
    date_of_service: string;
    match_score: number;
    match_reasons: string[];
}

interface LineItem {
    id: string;
    description: string;
    amount: number;
    category: string;
    eligibility_status: 'needs_review' | 'eligible' | 'ineligible';
}

interface ImportItem {
    productName: string;
    price: number;
    orderDate: string;
    category: string;
    reasoning: string;
}

interface ImportItemWithStatus extends ImportItem {
    id: string;
    status: 'pending' | 'duplicate' | 'saved' | 'skipped';
    duplicateMatch?: DuplicateMatch;
    editing: boolean;
    provider: string;
    familyMember: string;
}

const CATEGORIES = [
    { value: 'medical', label: 'Medical' },
    { value: 'dental', label: 'Dental' },
    { value: 'vision', label: 'Vision' },
    { value: 'prescriptions', label: 'Prescriptions' },
    { value: 'equipment', label: 'Equipment' },
    { value: 'otc', label: 'Over-the-Counter' },
    { value: 'other', label: 'Other' },
];

const FAMILY_MEMBERS = [
    { value: '', label: 'Select family member' },
    { value: 'self', label: 'Self' },
    { value: 'spouse', label: 'Spouse' },
    { value: 'dependent', label: 'Dependent' },
];

// Session storage key for pending imports
const IMPORT_KEY = 'hsa_pending_imports';

// Format date as "Jan 15, 2026"
function formatDate(dateString: string): string {
    if (!dateString) return '-';
    const date = new Date(dateString + 'T00:00:00'); // Ensure local timezone
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });
}

export default function Upload() { return <ReceiptRecoveryGate><UploadContent /></ReceiptRecoveryGate>; }

function UploadContent() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const api = useApi();
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Check if we're in import mode
    const isImportMode = searchParams.get('mode') === 'import';

    // Import mode state
    const [importItems, setImportItems] = useState<ImportItemWithStatus[]>([]);
    const [checkingDuplicates, setCheckingDuplicates] = useState(false);

    // Mode toggle: 'image' or 'text'
    const [mode, setMode] = useState<'image' | 'text'>('image');

    const [file, setFile] = useState<File | null>(null);
    const [preview, setPreview] = useState<string | null>(null);
    useEffect(() => {
        if (!file) { setPreview(null); return; }
        const url = URL.createObjectURL(file);
        setPreview(url);
        return () => URL.revokeObjectURL(url);
    }, [file]);
    const [textInput, setTextInput] = useState('');
    const [extracting, setExtracting] = useState(false);
    const [extracted, setExtracted] = useState<OcrResult | null>(null);
    const [extractNotice, setExtractNotice] = useState('');
    const [extractError, setExtractError] = useState('');
    const [duplicates, setDuplicates] = useState<DuplicateMatch[]>([]);
    const [showDuplicateModal, setShowDuplicateModal] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    const [saveStarted, setSaveStarted] = useState(false);
    const saveSession = useRef<ReceiptSaveSession | null>(null);
    const saveBusy = useRef(false);
    const extractionBusy = useRef(false);
    const [dragActive, setDragActive] = useState(false);

    function startManualEntry() {
        handleExtractionResult({date_of_service:null,provider:null,amount:null,
            document_type:'receipt',confidence:0,items:[]});
        setExtractNotice('Manual entry does not use external OCR. Review all details. Use synthetic fixtures only during this readiness pilot.');
    }

    // Line items for editing
    const [lineItems, setLineItems] = useState<LineItem[]>([]);

    // Form state
    const [formData, setFormData] = useState({
        date_of_service: '',
        provider: '',
        patient_name: '',
        amount: 0,
        category: '',
        family_member: '',
    });

    const handleDrag = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        const files = e.dataTransfer.files;
        if (files?.[0]) {
            handleFile(files[0]);
        }
    }, [extracting]);

    const handlePaste = useCallback((e: React.ClipboardEvent) => {
        if (mode === 'text') return; // Don't intercept paste in text mode
        const items = e.clipboardData.items;
        for (const item of items) {
            if (item.type.startsWith('image/')) {
                const file = item.getAsFile();
                if (file) handleFile(file);
                break;
            }
        }
    }, [mode, extracting]);

    async function handleFile(selectedFile: File) {
        if (saveSession.current || saveBusy.current || extractionBusy.current) return;
        extractionBusy.current = true;
        setFile(selectedFile);
        setExtracted(null);
        setExtractNotice('');
        setExtractError('');
        setLineItems([]);

        // Extract data
        setExtracting(true);
        try {
            const {extractLocalReceipt} = await import('../utils/localReceipt');
            const local = await extractLocalReceipt(selectedFile);
            const result = await api.extractFromReceipt(selectedFile, local.text);
            if (result.success && result.data) {
                const data = result.data as OcrResult;
                handleExtractionResult(data);
            } else {
                setExtractError(result.message || 'Unable to extract data from this file.');
            }
        } catch (error) {
            // Do not log receipt text or decoder errors.
            setExtractError('Local extraction failed or exceeded limits. Keep the original and enter details manually.');
        } finally {
            extractionBusy.current = false;
            setExtracting(false);
        }
    }

    async function handleTextExtract() {
        if (!textInput.trim() || extractionBusy.current) return;
        extractionBusy.current = true;

        setExtracting(true);
        setExtractError('');
        setExtractNotice('');

        try {
            const result = await api.extractFromText(textInput);
            if (result.success && result.data) {
                const data = result.data as OcrResult;
                handleExtractionResult(data);
            } else {
                setExtractError(result.message || 'Unable to parse the text.');
            }
        } catch (error) {
            // Do not log extracted medical data.
            setExtractError('Unable to parse the text right now. Please try again.');
        } finally {
            extractionBusy.current = false;
            setExtracting(false);
        }
    }

    function handleExtractionResult(data: OcrResult) {
        setExtracted(data);
        setExtractNotice(data.warnings?.join(' ') || 'Review all extracted fields before saving.');

        // Use extracted category or default based on items
        const extractedCategory = data.category || '';

        // Convert items to line items for editing
        const items: LineItem[] = data.items.map((item, idx) => ({
            id: `${Date.now()}-${idx}`,
            description: item.description,
            amount: item.amount,
            category: extractedCategory || 'medical',
            eligibility_status: 'needs_review',
        }));
        setLineItems(items);

        // Never manufacture a total from uncertain line items.
        const amount = data.amount ?? 0;

        setFormData({
            date_of_service: data.date_of_service || '',
            provider: data.provider || '',
            patient_name: '',
            amount: amount,
            category: extractedCategory,
            family_member: '',
        });
    }

    function updateLineItem(id: string, field: keyof LineItem, value: unknown) {
        setLineItems(items => {
            const updated = items.map(item =>
                item.id === id ? { ...item, [field]: value } : item
            );
            return updated;
        });
    }

    function deleteLineItem(id: string) {
        setLineItems(items => {
            const updated = items.filter(item => item.id !== id);
            return updated;
        });
    }

    function addLineItem() {
        const newItem: LineItem = {
            id: Date.now().toString(),
            description: 'New Item',
            amount: 0,
            category: 'medical',
            eligibility_status: 'needs_review',
        };
        setLineItems(items => [...items, newItem]);
    }

    async function checkDuplicates() {
        if (!formData.date_of_service || !formData.amount) return false;

        try {
            const result = await api.checkDuplicates({
                date_of_service: formData.date_of_service,
                amount: formData.amount,
                provider: formData.provider,
            });

            if (!result.success || !result.data) throw new Error('Duplicate check failed. Retry before saving.');
            if (result.success && result.data) {
                const data = result.data as { matches: DuplicateMatch[]; has_duplicates: boolean };
                if (data.has_duplicates) {
                    setDuplicates(data.matches);
                    setShowDuplicateModal(true);
                    return true;
                }
            }
        } catch (error) {
            throw new Error('Unable to check duplicates. Retry before saving.');
        }

        return false;
    }

    async function saveExpense(skipDuplicateCheck = false, targetId?: string) {
        if (saveBusy.current || extractionBusy.current) return;
        saveBusy.current = true;
        setSaving(true);
        setSaveError('');
        try {
            if (!saveSession.current) {
                if (!skipDuplicateCheck && await checkDuplicates()) return;
                const draft = {
                    ...formData,
                    hsa_eligible: 0,
                    eligibility_reasoning: 'Requires manual eligibility, payment and reimbursement review.',
                    items: lineItems.map(item => ({ description: item.description, total: item.amount,
                        category: item.category, eligibility_status: item.eligibility_status })),
                };
                validateDraft(draft);
                saveSession.current = new ReceiptSaveSession(api, draft, file,
                    extracted?.document_type || 'receipt', targetId);
                setSaveStarted(true);
            }
            const id = await saveSession.current.save();
            navigate(`/expenses/${id}`);
        } catch (error) {
            setSaveError(error instanceof Error ? error.message : 'Save interrupted. Retry this save.');
        } finally {
            saveBusy.current = false;
            setSaving(false);
        }
    }

    async function handleMerge(targetId: string) {
        setShowDuplicateModal(false);
        await saveExpense(true, targetId);
    }

    function reset() {
        if (saveBusy.current || extractionBusy.current) return;
        saveSession.current = null;
        setSaveStarted(false);
        setSaveError('');
        setFile(null);
        setPreview(null);
        setTextInput('');
        setExtracted(null);
        setExtractNotice('');
        setExtractError('');
        setLineItems([]);
        setFormData({
            date_of_service: '',
            provider: '',
            patient_name: '',
            amount: 0,
            category: '',
            family_member: '',
        });
        if (fileInputRef.current) fileInputRef.current.value = '';
    }

    function switchMode(newMode: 'image' | 'text') {
        if (extractionBusy.current) return;
        reset();
        setMode(newMode);
    }

    // Load import items from session storage when in import mode
    useEffect(() => {
        if (isImportMode) {
            const saved = sessionStorage.getItem(IMPORT_KEY);
            if (saved) {
                try {
                    const items: ImportItem[] = JSON.parse(saved);
                    const itemsWithStatus: ImportItemWithStatus[] = items.map((item, idx) => ({
                        ...item,
                        id: `import-${idx}`,
                        status: 'pending' as const,
                        editing: false,
                        provider: 'Amazon', // Default provider for imports
                        familyMember: 'self',
                    }));
                    setImportItems(itemsWithStatus);

                    // Check for duplicates
                    checkAllDuplicates(itemsWithStatus);
                } catch (e) {
                    console.error('Failed to parse import items:', e);
                }
            }
        }
    }, [isImportMode]);

    // Check all items for duplicates
    async function checkAllDuplicates(items: ImportItemWithStatus[]) {
        setCheckingDuplicates(true);

        for (const item of items) {
            try {
                const result = await api.checkDuplicates({
                    date_of_service: item.orderDate,
                    amount: item.price,
                    provider: item.provider,
                });

                if (result.success && result.data) {
                    const data = result.data as { matches: DuplicateMatch[]; has_duplicates: boolean };
                    if (data.has_duplicates && data.matches.length > 0) {
                        setImportItems(prev => prev.map(p =>
                            p.id === item.id
                                ? { ...p, status: 'duplicate' as const, duplicateMatch: data.matches[0] }
                                : p
                        ));
                    }
                }
            } catch (error) {
                console.error('Duplicate check failed for', item.productName, error);
            }
        }

        setCheckingDuplicates(false);
    }

    // Update an import item field
    function updateImportItem(id: string, field: keyof ImportItemWithStatus, value: unknown) {
        setImportItems(prev => prev.map(item =>
            item.id === id ? { ...item, [field]: value } : item
        ));
    }

    // Save a single import item as expense
    async function saveImportItem(item: ImportItemWithStatus) {
        try {
            const expenseData = {
                date_of_service: item.orderDate,
                provider: item.provider,
                patient_name: item.productName.substring(0, 100), // Use product name as description
                amount: item.price,
                category: item.category.toLowerCase(),
                family_member: item.familyMember,
            };

            const result = await api.createExpense(expenseData);
            if (result.success) {
                setImportItems(prev => prev.map(p =>
                    p.id === item.id ? { ...p, status: 'saved' as const, editing: false } : p
                ));
            }
        } catch (error) {
            console.error('Failed to save import item:', error);
            alert(`Failed to save: ${item.productName}`);
        }
    }

    // Skip an import item
    function skipImportItem(id: string) {
        setImportItems(prev => prev.map(item =>
            item.id === id ? { ...item, status: 'skipped' as const } : item
        ));
    }

    // Save all pending items
    async function saveAllPendingItems() {
        const pendingItems = importItems.filter(item => item.status === 'pending');
        for (const item of pendingItems) {
            await saveImportItem(item);
        }
    }

    // Clear import session and go back
    function cancelImport() {
        sessionStorage.removeItem(IMPORT_KEY);
        navigate('/eligibility');
    }

    // Finish import
    function finishImport() {
        sessionStorage.removeItem(IMPORT_KEY);
        navigate('/expenses');
    }

    // If we're in import mode, show the import review UI
    if (isImportMode && importItems.length > 0) {
        const pendingItems = importItems.filter(i => i.status === 'pending' || i.status === 'duplicate');
        const savedItems = importItems.filter(i => i.status === 'saved');
        const skippedItems = importItems.filter(i => i.status === 'skipped');

        return (
            <div className="upload-page">
                <header className="page-header">
                    <h1>Review Imports</h1>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                        Review each item before adding to your expenses. {checkingDuplicates && '(Checking for duplicates...)'}
                    </p>
                </header>

                {/* Progress Summary */}
                <div className="import-progress-bar" style={{
                    display: 'flex',
                    gap: '1rem',
                    marginBottom: '1.5rem',
                    padding: '1rem',
                    background: 'var(--bg-card)',
                    borderRadius: 'var(--radius-lg)',
                    border: '1px solid var(--border)'
                }}>
                    <div style={{ flex: 1 }}>
                        <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>Pending</span>
                        <div style={{ fontSize: '1.25rem', fontWeight: 600 }}>{pendingItems.length}</div>
                    </div>
                    <div style={{ flex: 1 }}>
                        <span style={{ color: 'var(--green)', fontSize: '0.75rem' }}>Saved</span>
                        <div style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--green)' }}>{savedItems.length}</div>
                    </div>
                    <div style={{ flex: 1 }}>
                        <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>Skipped</span>
                        <div style={{ fontSize: '1.25rem', fontWeight: 600 }}>{skippedItems.length}</div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        {pendingItems.length > 0 && (
                            <button className="btn btn-primary" onClick={saveAllPendingItems}>
                                Save All ({pendingItems.length})
                            </button>
                        )}
                        {pendingItems.length === 0 && (
                            <button className="btn btn-primary" onClick={finishImport}>
                                ✓ Done
                            </button>
                        )}
                        <button className="btn btn-secondary" onClick={cancelImport}>
                            Cancel
                        </button>
                    </div>
                </div>

                {/* Import Items List */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {importItems.map(item => (
                        <div
                            key={item.id}
                            className="card"
                            style={{
                                opacity: item.status === 'saved' || item.status === 'skipped' ? 0.6 : 1,
                                borderLeft: item.status === 'duplicate' ? '3px solid var(--yellow)' :
                                    item.status === 'saved' ? '3px solid var(--green)' : 'none'
                            }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                                <div style={{ flex: 1 }}>
                                    <h3 style={{ fontSize: '0.95rem', marginBottom: '0.25rem', color: 'var(--text-primary)' }}>
                                        {item.productName}
                                    </h3>
                                    <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: 0 }}>
                                        {item.reasoning}
                                    </p>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                    <div style={{ fontSize: '1.1rem', fontWeight: 600 }}>${item.price.toFixed(2)}</div>
                                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{formatDate(item.orderDate)}</div>
                                </div>
                            </div>

                            {/* Duplicate Warning */}
                            {item.status === 'duplicate' && item.duplicateMatch && (
                                <div style={{
                                    background: 'var(--yellow-bg)',
                                    padding: '0.75rem',
                                    borderRadius: 'var(--radius-md)',
                                    marginBottom: '0.75rem',
                                    fontSize: '0.85rem'
                                }}>
                                    ⚠️ Possible duplicate: {item.duplicateMatch.provider} - ${item.duplicateMatch.amount} on {formatDate(item.duplicateMatch.date_of_service)}
                                </div>
                            )}

                            {/* Status badges */}
                            {item.status === 'saved' && (
                                <div style={{ color: 'var(--green)', fontWeight: 500 }}>✓ Saved</div>
                            )}
                            {item.status === 'skipped' && (
                                <div style={{ color: 'var(--text-muted)' }}>Skipped</div>
                            )}

                            {/* Edit form or action buttons */}
                            {(item.status === 'pending' || item.status === 'duplicate') && (
                                <>
                                    {item.editing ? (
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', marginTop: '0.75rem' }}>
                                            <div className="form-group" style={{ marginBottom: 0 }}>
                                                <label style={{ fontSize: '0.75rem' }}>Provider</label>
                                                <input
                                                    type="text"
                                                    value={item.provider}
                                                    onChange={(e) => updateImportItem(item.id, 'provider', e.target.value)}
                                                    style={{ padding: '0.5rem' }}
                                                />
                                            </div>
                                            <div className="form-group" style={{ marginBottom: 0 }}>
                                                <label style={{ fontSize: '0.75rem' }}>Date</label>
                                                <input
                                                    type="date"
                                                    value={item.orderDate}
                                                    onChange={(e) => updateImportItem(item.id, 'orderDate', e.target.value)}
                                                    style={{ padding: '0.5rem' }}
                                                />
                                            </div>
                                            <div className="form-group" style={{ marginBottom: 0 }}>
                                                <label style={{ fontSize: '0.75rem' }}>Category</label>
                                                <select
                                                    value={item.category}
                                                    onChange={(e) => updateImportItem(item.id, 'category', e.target.value)}
                                                    style={{ padding: '0.5rem' }}
                                                >
                                                    {CATEGORIES.map(cat => (
                                                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    ) : null}

                                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
                                        <button
                                            className="btn btn-primary btn-sm"
                                            onClick={() => saveImportItem(item)}
                                        >
                                            ✓ Save
                                        </button>
                                        <button
                                            className="btn btn-secondary btn-sm"
                                            onClick={() => updateImportItem(item.id, 'editing', !item.editing)}
                                        >
                                            {item.editing ? 'Hide Edit' : '✏️ Edit'}
                                        </button>
                                        <button
                                            className="btn btn-secondary btn-sm"
                                            onClick={() => skipImportItem(item.id)}
                                            style={{ marginLeft: 'auto' }}
                                        >
                                            Skip
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    // If we have extracted data, show the review/edit form
    if (extracted) {
        return (
            <div className="upload-page" onPaste={handlePaste}>
                <header className="page-header">
                    <h1>Verify & Edit</h1>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                        Review the receipt details before saving.
                    </p>
                </header>

                {extractNotice && <p role="status">{extractNotice}</p>}
                <div className="upload-grid">
                    {/* Left side: Form */}
                    <div className="card">
                        <fieldset disabled={saving || saveStarted} style={{ border: 0, padding: 0, minWidth: 0 }}>
                        <div className="form-group">
                            <label>Merchant / Provider</label>
                            <input
                                type="text"
                                value={formData.provider}
                                onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
                                placeholder="e.g., CVS Pharmacy, Dr. Smith"
                            />
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                            <div className="form-group">
                                <label>Date of Service</label>
                                <input
                                    type="date"
                                    value={formData.date_of_service}
                                    onChange={(e) => setFormData({ ...formData, date_of_service: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <label>Patient / Family Member</label>
                                <input
                                    type="text"
                                    value={formData.patient_name}
                                    onChange={(e) => setFormData({ ...formData, patient_name: e.target.value })}
                                    placeholder="e.g., Self, Spouse, Child"
                                />
                            </div>
                        </div>

                        {/* Line Items Editor */}
                        <div className="line-items-editor">
                            <div className="line-items-header">
                                <h3>Line Items</h3>
                                <button type="button" className="add-item-btn" onClick={addLineItem}>
                                    ➕ Add Item
                                </button>
                            </div>

                            {lineItems.map((item) => (
                                <div key={item.id} className="line-item">
                                    <div className="line-item-row">
                                        <input
                                            type="text"
                                            value={item.description}
                                            onChange={(e) => updateLineItem(item.id, 'description', e.target.value)}
                                            placeholder="Item description"
                                        />
                                        <span style={{ color: 'var(--text-muted)' }}>$</span>
                                        <input
                                            type="number"
                                            className="amount-input"
                                            value={item.amount}
                                            onChange={(e) => updateLineItem(item.id, 'amount', parseFloat(e.target.value) || 0)}
                                            step="0.01"
                                        />
                                    </div>
                                    <div className="line-item-controls">
                                        <select
                                            value={item.category}
                                            onChange={(e) => updateLineItem(item.id, 'category', e.target.value)}
                                        >
                                            {CATEGORIES.map((cat) => (
                                                <option key={cat.value} value={cat.value}>{cat.label}</option>
                                            ))}
                                        </select>
                                        <select aria-label="Item eligibility review"
                                            value={item.eligibility_status}
                                            onChange={e => updateLineItem(item.id, 'eligibility_status', e.target.value)}>
                                            <option value="needs_review">Needs review</option>
                                            <option value="eligible">Reviewed: eligible</option>
                                            <option value="ineligible">Reviewed: ineligible</option>
                                        </select>
                                        <button
                                            type="button"
                                            className="delete-item-btn"
                                            onClick={() => deleteLineItem(item.id)}
                                            aria-label="Delete item"
                                        >
                                            🗑️
                                        </button>
                                    </div>
                                </div>
                            ))}

                            {lineItems.length === 0 && (
                                <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '1rem' }}>
                                    No line items. Click "Add Item" to add manually.
                                </p>
                            )}

                            <div className="line-items-total">
                                <label htmlFor="receipt-total">Original receipt total</label>
                                <input id="receipt-total" type="number" min="0.01" step="0.01" value={formData.amount}
                                    onChange={e => setFormData({...formData, amount:Number(e.target.value)})} />
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '1rem' }}>
                            <div className="form-group">
                                <label>Category</label>
                                <select
                                    value={formData.category}
                                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                >
                                    <option value="">Select category</option>
                                    {CATEGORIES.map((cat) => (
                                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Family Member</label>
                                <select
                                    value={formData.family_member}
                                    onChange={(e) => setFormData({ ...formData, family_member: e.target.value })}
                                >
                                    {FAMILY_MEMBERS.map((member) => (
                                        <option key={member.value} value={member.value}>{member.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        </fieldset>
                        {saveError && <div className="error-message" role="alert">{saveError}</div>}
                        {saveStarted && <p role="status">Retry uses the same expense and original file.
                            The server retains this pending save across reloads. Discard may leave an expense without its receipt; use recovery to inspect it.</p>}
                        <p>Items require review. Saving records documentation; it does not approve reimbursement.</p>
                        <div className="form-actions">
                            <button type="button" className="btn btn-secondary" onClick={() => { if (saveStarted) window.location.reload(); else reset(); }} disabled={saving}>
                                {saveStarted ? 'Open Save Recovery' : 'Discard'}
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => saveExpense()}
                                disabled={saving}
                            >
                                {saving ? 'Saving...' : saveStarted ? 'Retry Save' : '✓ Confirm & Save'}
                            </button>
                        </div>
                    </div>

                    {/* Right side: Document preview */}
                    <div className="card">
                        <h2>Original Document</h2>
                        {preview && file && <a href={preview} download={file.name}>Download original for review</a>}
                        {mode === 'image' && preview ? (
                            <div style={{
                                background: 'var(--bg-input)',
                                borderRadius: 'var(--radius-lg)',
                                minHeight: '300px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'hidden'
                            }}>
                                {file?.type === 'application/pdf' ? (
                                    <div style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
                                        <span style={{ fontSize: '3rem' }}>📄</span>
                                        <p>PDF Document</p>
                                        <p style={{ fontSize: '0.75rem' }}>{file.name}</p>
                                    </div>
                                ) : (
                                    <img
                                        src={preview}
                                        alt="Receipt"
                                        style={{ maxWidth: '100%', maxHeight: '400px', objectFit: 'contain' }}
                                    />
                                )}
                            </div>
                        ) : (
                            <div style={{
                                background: 'var(--bg-input)',
                                borderRadius: 'var(--radius-lg)',
                                padding: '1rem',
                                fontFamily: 'monospace',
                                fontSize: '0.75rem',
                                color: 'var(--text-secondary)',
                                maxHeight: '400px',
                                overflow: 'auto',
                                whiteSpace: 'pre-wrap'
                            }}>
                                {textInput || extracted.raw_text || 'No text available'}
                            </div>
                        )}

                        {extracted.confidence > 0 && (
                            <p style={{ marginTop: '1rem', fontSize: '0.875rem', color: 'var(--text-muted)' }}>
                                Extraction confidence: {extracted.confidence}%
                            </p>
                        )}
                    </div>
                </div>

                {/* Duplicate Modal */}
                {showDuplicateModal && (
                    <DuplicateReviewModal
                        matches={duplicates}
                        onMerge={handleMerge}
                        onKeepSeparate={() => {
                            setShowDuplicateModal(false);
                            saveExpense(true);
                        }}
                        onCancel={() => setShowDuplicateModal(false)}
                    />
                )}
            </div>
        );
    }

    return (
        <div className="upload-page" onPaste={handlePaste}>
            <header className="page-header">
                <h1>Upload Receipt</h1>
            </header>

            <p role="status">Image and PDF text extraction runs locally. Review every field and all original pages. Payment and eligibility require separate review. Saving retains the original file.</p>
            <button type="button" className="btn btn-secondary" onClick={startManualEntry} disabled={extracting}>Enter details manually</button>
            {/* Mode Toggle */}
            <div className="upload-mode-toggle">
                <div className="mode-toggle-container">
                    <button
                        type="button"
                        className={`mode-toggle-btn ${mode === 'image' ? 'active' : ''}`}
                        onClick={() => switchMode('image')}
                    >
                        📄 Upload Document
                    </button>
                    <button
                        type="button"
                        className={`mode-toggle-btn ${mode === 'text' ? 'active' : ''}`}
                        onClick={() => switchMode('text')}
                    >
                        📝 Paste Text
                    </button>
                </div>
            </div>

            {mode === 'image' ? (
                <div className="upload-grid">
                    {/* Upload Zone */}
                    <div className="card upload-zone-card" style={{ position: 'relative' }}>
                        {!file ? (
                            <div
                                className={`upload-zone ${dragActive ? 'active' : ''}`}
                                onDragEnter={handleDrag}
                                onDragLeave={handleDrag}
                                onDragOver={handleDrag}
                                onDrop={handleDrop}
                                onClick={() => fileInputRef.current?.click()}
                                role="button"
                                tabIndex={0}
                                aria-label="Click or drag to upload receipt"
                            >
                                <div className="upload-icon">📤</div>
                                <h2>Click to upload</h2>
                                <p>or drag & drop</p>
                                <p className="upload-hint">
                                    JPG, PNG and PDF local extraction. HEIC/TIFF may need manual entry.<br />
                                    Originals up to 10 MiB; PDFs up to 20 pages.<br />
                                    You can also paste from clipboard (Ctrl+V)
                                </p>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*,.pdf,.heic,.tiff"
                                    onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
                                    hidden
                                />

                                {/* Mobile camera button */}
                                <label className="camera-btn">
                                    📷 Take Photo
                                    <input
                                        type="file"
                                        accept="image/*"
                                        capture="environment"
                                        onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
                                        hidden
                                    />
                                </label>
                            </div>
                        ) : (
                            <div className="file-preview">
                                {preview && file.type.startsWith('image/') && <img src={preview} alt="Receipt preview" />}
                                {!file.type.startsWith('image/') && <div className="pdf-preview">📄 {file.name}</div>}
                                {preview && <a href={preview} download={file.name}>Download original for review</a>}
                                <button
                                    onClick={reset}
                                    className="btn btn-secondary"
                                >
                                    Remove
                                </button>
                            </div>
                        )}

                        {extracting && (
                            <div className="extracting-overlay">
                                <div className="spinner"></div>
                                <p>Extracting data...</p>
                            </div>
                        )}

                        {extractError && (
                            <div className="error-message" role="alert">
                                ⚠️ {extractError}
                            </div>
                        )}
                        {!extractError && extractNotice && (
                            <div className="notice-message" role="status">
                                {extractNotice}
                            </div>
                        )}
                    </div>

                    {/* Help info */}
                    <div className="card">
                        <h2>Tips for Best Results</h2>
                        <ul style={{
                            fontSize: '0.875rem',
                            color: 'var(--text-secondary)',
                            listStyle: 'none',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0.75rem'
                        }}>
                            <li>✓ Ensure the receipt is well-lit and in focus</li>
                            <li>✓ Include the full receipt with date and total visible</li>
                            <li>✓ For PDFs, text-based files work best</li>
                            <li>✓ You can also paste text if you have a digital receipt</li>
                        </ul>
                    </div>
                </div>
            ) : (
                <div style={{ maxWidth: '700px', margin: '0 auto' }}>
                    <div className="text-input-card">
                        <div className="form-group">
                            <label>Paste Receipt Text or CSV Row</label>
                            <textarea
                                value={textInput}
                                onChange={(e) => setTextInput(e.target.value)}
                                placeholder={`Example: CVS Pharmacy 01/15/2026
1. Bandages $5.99
2. Sunscreen SPF 50 $12.50
Total: $18.49

Or paste from an email, Amazon order, or any text...`}
                            />
                        </div>
                        <p className="text-hint">
                            Local parsing suggests merchant, date and items for your review.
                        </p>

                        {extractError && (
                            <div className="error-message" role="alert">
                                ⚠️ {extractError}
                            </div>
                        )}

                        <button
                            type="button"
                            className="btn btn-primary"
                            style={{ width: '100%', marginTop: '1rem' }}
                            onClick={handleTextExtract}
                            disabled={extracting || !textInput.trim()}
                        >
                            {extracting ? 'Processing...' : 'Parse Text Locally'}
                        </button>
                    </div>
                </div>
            )}

            {/* Duplicate Modal */}
            {showDuplicateModal && (
                <DuplicateReviewModal
                    matches={duplicates}
                    onMerge={handleMerge}
                    onKeepSeparate={() => {
                        setShowDuplicateModal(false);
                        saveExpense(true);
                    }}
                    onCancel={() => setShowDuplicateModal(false)}
                />
            )}
        </div>
    );
}
